import { useEffect, useState } from "react";
import { Cookie, ChevronDown, ChevronUp } from "lucide-react";

const COOKIES = [
  ["session_token", "Cookie", "Essential", "Keeps you securely signed in to your portal (team / client / vendor)", "Session"],
  ["gf_cookie_consent", "Local storage", "Essential", "Remembers your cookie choice so we don't ask again", "12 months"],
  ["gf_visitor_id", "Local storage", "Essential", "Anonymous id used only to record your consent for our audit trail", "12 months"],
  ["Google reCAPTCHA (google.com)", "Third-party cookie", "Essential", "Invisible bot protection for our forms and logins", "Session"],
  ["Activity & preferences", "Local storage", "Personalisation — optional", "Pages and tools you use, to personalise your learning and recommendations. Set ONLY if you accept all", "12 months"],
];

export const CookieConsent = () => {
  const [show, setShow] = useState(false);
  const [details, setDetails] = useState(false);
  useEffect(() => {
    const p = window.location.pathname;
    const signingRoute = ["/agreement-sign/", "/agreement-review/", "/matrix-signoff/", "/broker-quote/", "/supplier-portal/", "/vendor-hub/", "/teamportal", "/admin", "/ea/"].some((r) => p.startsWith(r));
    if (!signingRoute && !localStorage.getItem("gf_cookie_consent")) setShow(true);
  }, []);
  const decide = async (choice) => {
    let vid = localStorage.getItem("gf_visitor_id");
    if (!vid) {
      vid = (window.crypto?.randomUUID && window.crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem("gf_visitor_id", vid);
    }
    localStorage.setItem("gf_cookie_consent", choice);
    setShow(false);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/consent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ choice, visitor_id: vid, path: window.location.pathname }),
      });
    } catch { /* consent stored locally regardless */ }
  };
  if (!show) return null;
  return (
    <div className="fixed bottom-0 inset-x-0 z-[100] px-4 pb-4" data-testid="cookie-consent-banner">
      <div className="max-w-3xl mx-auto bg-gf-surface/95 backdrop-blur-md border border-gf-primary/30 p-4 sm:p-5 shadow-2xl">
        <div className="flex flex-wrap items-center gap-4">
          <p className="font-body text-xs sm:text-sm text-gf-text flex-1 min-w-[240px]">
            <Cookie className="w-4 h-4 inline mr-1.5 text-gf-primary -mt-0.5" />
            We use essential cookies to run the site and, with your consent, track your activity to personalise your
            learning and recommendations. See our{" "}
            <a href="/privacypolicy" className="text-gf-primary underline underline-offset-2 hover:opacity-80" data-testid="cookie-consent-privacy-link">Privacy Policy</a>.
          </p>
          <div className="flex items-center gap-2">
            <button onClick={() => decide("essential")} data-testid="cookie-consent-essential"
              className="border border-white/25 text-gf-muted hover:text-gf-text hover:border-white/40 font-body text-xs px-4 py-2 transition-colors">
              Essential only</button>
            <button onClick={() => decide("all")} data-testid="cookie-consent-accept"
              className="bg-gf-primary text-gf-bg font-body font-bold text-xs px-5 py-2 hover:opacity-90 transition-opacity">
              Accept all</button>
          </div>
        </div>
        <button onClick={() => setDetails(!details)} data-testid="cookie-consent-details-toggle"
          className="inline-flex items-center gap-1 font-mono text-[10px] uppercase text-gf-muted hover:text-gf-primary mt-2 transition-colors">
          {details ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />} What we store & why (GDPR)</button>
        {details && (
          <div className="mt-2 border-t border-white/10 pt-2 overflow-x-auto" data-testid="cookie-consent-details">
            <table className="w-full min-w-[560px] text-left">
              <thead><tr>{["Name", "Type", "Category", "Purpose", "Duration"].map((h) => (
                <th key={h} className="font-mono text-[8px] uppercase text-gf-muted pr-3 pb-1">{h}</th>))}</tr></thead>
              <tbody>
                {COOKIES.map(([name, type, cat, why, dur]) => (
                  <tr key={name} className="border-t border-white/5">
                    <td className="font-mono text-[9px] text-gf-text pr-3 py-1">{name}</td>
                    <td className="font-body text-[9px] text-gf-muted pr-3 py-1">{type}</td>
                    <td className={`font-mono text-[8px] uppercase pr-3 py-1 ${cat.startsWith("Essential") ? "text-gf-primary" : "text-amber-400"}`}>{cat}</td>
                    <td className="font-body text-[9px] text-gf-muted pr-3 py-1">{why}</td>
                    <td className="font-body text-[9px] text-gf-muted py-1">{dur}</td>
                  </tr>))}
              </tbody>
            </table>
            <p className="font-body text-[9px] text-gf-muted mt-2">
              Legal basis: essential items are required to operate the site (legitimate interest, GDPR Art. 6(1)(f));
              personalisation runs only with your explicit consent (Art. 6(1)(a)). You can withdraw anytime by clearing
              this site's data in your browser — the banner will reappear and no personalisation runs until you consent
              again. Choosing "Essential only" means we never track your activity.</p>
          </div>)}
      </div>
    </div>
  );
};
